/**
 * A check-in the chat wrote, as its card (docs/chats.md §4, Decision 10 of
 * the plan): drawn from a `checkin_created` / `checkin_updated` notice.
 *
 * The card is the check-in as it is now, read by id, so it shows the tags
 * once the parse has run. While the parse the apply enqueued is still live,
 * the card watches its job (`useParseActivity().watchJob`) and draws the
 * parse panel the composer and Genie draw; when the job ends the check-in is
 * read again. A parse that finished before the card mounted — a reload — is
 * not watched at all. The parse's `checkin_parsed` notice (`parsedSeq`, its
 * seq) also reads it again, so the tags show even when the watch was missed.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
    fetchJobActivity,
    getAllFeatures,
    getCheckinsByIds,
    getProjects,
    type Checkin,
    type Feature,
    type Project,
} from '../../api/client'
import { emptyParseActivity, useParseActivity, type ParseActivity } from '../../hooks/useParseActivity'
import ChatCheckinView from '../ChatCheckinView'
import { ChatNote } from './ChatItemView'

/** Shown while the parse has said nothing yet. */
const ANALYZING_ONLY: ParseActivity = emptyParseActivity(0)

// One features and projects list for every card on the page, refreshed
// after a parse (which can create features).
let featuresPromise: Promise<Feature[]> | null = null
let projectsPromise: Promise<Project[]> | null = null
const loadFeatures = (fresh = false): Promise<Feature[]> => {
    if (!featuresPromise || fresh) {
        featuresPromise = getAllFeatures().catch(() => {
            featuresPromise = null
            return []
        })
    }
    return featuresPromise
}
const loadProjects = (fresh = false): Promise<Project[]> => {
    if (!projectsPromise || fresh) {
        projectsPromise = getProjects().catch(() => {
            projectsPromise = null
            return []
        })
    }
    return projectsPromise
}

/** A check-in read by id carries its rows, not the features and projects
 *  they name; the card draws its chips from those. */
const withDetails = (checkin: Checkin, features: Feature[], projects: Project[]): Checkin => {
    const featureById = new Map(features.map((f) => [f.id, f]))
    const projectById = new Map(projects.map((p) => [p.id, p]))
    return {
        ...checkin,
        feature_details: (checkin.checkin_features ?? [])
            .map((cf) => featureById.get(cf.feature_id))
            .filter((f): f is Feature => f != null),
        project_details: (checkin.checkin_projects ?? [])
            .map((cp) => projectById.get(cp.project_id))
            .filter((p): p is Project => p != null),
    }
}

const formatDate = (dateString: string, includeYearIfDifferent: boolean = false): string => {
    if (!dateString) return ''
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    const showYear = includeYearIfDifferent && year !== new Date().getFullYear()
    return date.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        ...(showYear && { year: 'numeric' }),
    })
}

const LIVE_JOB = ['queued', 'running']

interface CheckinEventCardProps {
    checkinId: number
    parseJobId: string | null
    parsedSeq?: number | null
}

const CheckinEventCard: React.FC<CheckinEventCardProps> = ({ checkinId, parseJobId, parsedSeq = null }) => {
    const { activities, watchJob, setExpanded, setTechnical } = useParseActivity()
    const [checkin, setCheckin] = useState<Checkin | null>(null)
    const [collectionItems, setCollectionItems] = useState<Record<string, any>>({})
    const [features, setFeatures] = useState<Feature[]>([])
    const [missing, setMissing] = useState(false)
    const [analyzing, setAnalyzing] = useState(false)
    const [peek, setPeek] = useState(false)
    const mounted = useRef(true)
    const parsedAtMount = useRef(parsedSeq)

    const load = useCallback(
        async (freshFeatures = false) => {
            const [fetched, allFeatures, allProjects] = await Promise.all([
                getCheckinsByIds([checkinId]).catch(() => null),
                loadFeatures(freshFeatures),
                loadProjects(freshFeatures),
            ])
            if (!mounted.current) return
            setFeatures(allFeatures)
            const found = fetched?.checkins.find((c) => c.id === checkinId) ?? null
            if (fetched && !found) setMissing(true)
            setCheckin(found ? withDetails(found, allFeatures, allProjects) : null)
            setCollectionItems(fetched?.collection_items ?? {})
        },
        [checkinId],
    )

    useEffect(() => {
        mounted.current = true
        void load()
        if (parseJobId) {
            void (async () => {
                const stored = await fetchJobActivity(parseJobId).catch(() => null)
                if (!mounted.current || !stored || !LIVE_JOB.includes(stored.job_status)) return
                setAnalyzing(true)
                try {
                    await watchJob(checkinId, parseJobId)
                } catch {
                    // Failed or dropped: the check-in is shown as it stands.
                } finally {
                    if (mounted.current) {
                        setAnalyzing(false)
                        void load(true)
                    }
                }
            })()
        }
        return () => {
            mounted.current = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkinId, parseJobId])

    useEffect(() => {
        if (parsedSeq == null || parsedSeq === parsedAtMount.current) return
        parsedAtMount.current = parsedSeq
        setAnalyzing(false)
        void load(true)
    }, [parsedSeq, load])

    if (missing) return <ChatNote>A check-in saved here was later deleted.</ChatNote>
    if (!checkin) return null

    const record = activities[checkinId]
    const activity = analyzing ? (record && record.status === 'running' ? record : ANALYZING_ONLY) : undefined

    return (
        <div
            className="mb-4 rounded-lg border border-gray-200 bg-white/90 px-3 pt-3"
            data-testid="chat-checkin-card"
            data-checkin-id={checkinId}
        >
            <ChatCheckinView
                snapshotCheckin={checkin}
                snapshotCollectionItems={collectionItems}
                suppressToggle
                // The timeline's spine, without its outer padding: the card supplies that.
                spineClass="sm:border-l-2 sm:border-action sm:pl-4"
                onPeek={() => setPeek(true)}
                peekOpen={peek}
                onPeekClose={() => setPeek(false)}
                index={0}
                showSeparator={false}
                editingCheckinId={null}
                showNewCheckinComposer={false}
                features={features}
                recentFeatures={[]}
                sortOrder="most_recent"
                savingState={null}
                activity={activity}
                onToggleParseExpanded={(expanded: boolean) => setExpanded(checkinId, expanded)}
                onToggleParseTechnical={(technical: boolean) => setTechnical(checkinId, technical)}
                showMenu={true}
                availableActions={['edit-in-page']}
                formatDate={formatDate}
                getComposerCheckinFromCheckin={() => ({ body: '', applies_to: '', features: [], projects: [] })}
                handleFieldEdit={() => {}}
                handleDeleteCheckin={() => {}}
                handleEditCheckin={() => {}}
                handleDuplicateCheckin={() => {}}
                handleFilterSelect={() => {}}
                handleReanalyzeCheckin={() => {}}
                onRefresh={() => void load()}
                setEditingCheckinId={() => {}}
                setCheckins={() => {}}
                setShowNewCheckinComposer={() => {}}
                firstElementIsEmptyCheckin={() => false}
                isSelectionMode={false}
                isSelected={false}
                onToggleSelection={() => {}}
            />
        </div>
    )
}

export default CheckinEventCard
